import { ArrowUpIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  FormLabel,
  Icon,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { ColumnDef, flexRender } from "@tanstack/react-table";
import IconArrowRightUp from "assets/icons/icon-arrow-right-up.svg?react";
import IconStatClicked from "assets/icons/icon-stat-clicked.svg?react";
import IconStatDelivered from "assets/icons/icon-stat-delivered.svg?react";
import IconStatDropped from "assets/icons/icon-stat-dropped.svg?react";
import IconStatOpened from "assets/icons/icon-stat-opened.svg?react";
import IconStatSent from "assets/icons/icon-stat-sent.svg?react";
import TagEmailEvent, { EmailEvents } from "components/TagEmailEvent";
import CheckboxSelect from "components/ui/CheckboxSelect/CheckboxSelect";
import DateTime from "components/ui/DateTime";
import EmptyList from "components/ui/EmptyList";
import FilterGroup from "components/ui/FilterGroup";
import Pagination from "components/ui/Pagination";
import SearchInput from "components/ui/SearchInput";
import Stat from "components/ui/Stat";
import config from "config";
import {
  TableProvider,
  useDynamicTableContext,
} from "contexts/DynamicTableContext";
import { useMessages } from "hooks/useMessages";
import React from "react";
import { useEmailSentReport } from "../hooks/useEmailSentReport";

interface Props {
  email: any;
}

export default function EmailSentStats({ email }: Props) {
  const columns = React.useMemo<ColumnDef<any>[]>(
    () => [
      {
        header: "Data do evento",
        accessorKey: "date",
        cell: (props) => <DateTime date={props.cell.getValue()} noBreak />,
        enableSorting: true,
      },
      {
        header: "Evento",
        accessorKey: "name",
        cell: (props) => <TagEmailEvent event={props.cell.getValue()} />,
        filterFn: "arrIncludesSome",
        enableSorting: true,
      },
      {
        header: "Nome",
        accessorKey: "recipientName",
        enableSorting: true,
      },
      {
        header: "E-mail",
        accessorKey: "recipientEmail",
        enableSorting: true,
      },
    ],
    []
  );

  return (
    <>
      <SimpleGrid
        columnGap="50px"
        rowGap="20px"
        columns={{ base: 2, md: 3, lg: 5 }}
      >
        <Stat
          icon={<IconStatSent />}
          label="Enviados"
          value={email._count.sent}
        />
        <Stat
          icon={<IconStatDelivered />}
          label="Entregues"
          value={email._count.delivered}
        />
        <Stat
          icon={<IconStatOpened />}
          label="Abertos"
          value={email._count.opened}
        />
        <Stat
          icon={<IconStatClicked />}
          label="Cliques"
          value={email._count.clicked}
        />
        <Stat
          icon={<IconStatDropped />}
          label="Bounce"
          value={email._count.bounced}
        />
      </SimpleGrid>

      {email?.emailEvents?.length > 0 && (
        <>
          <TableProvider
            columns={columns}
            data={email.emailEvents}
            defaultSorting={[{ id: "name", asc: true }]}
          >
            <Filters />
            <List emailId={email.id} />
          </TableProvider>
        </>
      )}
    </>
  );
}

function Filters() {
  const { table } = useDynamicTableContext();

  const columnEventName = table.getColumn("name");

  return (
    <FilterGroup
      justifyContent="space-between"
      zIndex={1}
      pos="relative"
      mt="35px"
    >
      <Box flex={1} minW="180px">
        <FormLabel>Buscar</FormLabel>
        <SearchInput
          placeholder="Buscar por nome ou e-mail"
          onChange={(value) => table.setGlobalFilter(String(value))}
        />
      </Box>
      <Box>
        <FormLabel>Filtrar por evento</FormLabel>
        <CheckboxSelect
          placeholder="Todos os status"
          options={Object.entries(EmailEvents).map(([key, value]) => ({
            value: key,
            label: value,
          }))}
          onChange={(value) =>
            columnEventName.setFilterValue(value.map((v) => v.value))
          }
        />
      </Box>
    </FilterGroup>
  );
}

function List({ emailId }: { emailId: string }) {
  const { onError } = useMessages();
  const { table } = useDynamicTableContext();
  const hasResult = table.getRowCount() > 0;

  const globalFilter = table.getState().globalFilter;
  const filters = table.getState().columnFilters.reduce(
    (acc, filter) => {
      acc[filter.id] = filter.value;
      return acc;
    },
    { globalFilter } as Record<string, any>
  );

  const { onGenerateEmailSentReport, isPending } = useEmailSentReport({
    onSuccess: (filepath: string) => {
      window.location.assign(`${config.api}/${filepath}`);
    },
    onError: (error) => onError(error?.response?.data?.message),
  });

  return (
    <>
      {hasResult && (
        <>
          <Flex>
            <Button
              mt="35px"
              size="sm"
              leftIcon={<IconArrowRightUp />}
              ml="auto"
              isDisabled={isPending}
              isLoading={isPending}
              onClick={() => onGenerateEmailSentReport({ emailId, filters })}
            >
              Exportar
            </Button>
          </Flex>
          <Table mt="10px">
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th key={header.id} colSpan={header.colSpan}>
                      <Box
                        pr="15px"
                        pos="relative"
                        {...{
                          className: header.column.getCanSort()
                            ? "cursor-pointer select-none"
                            : "",
                          onClick: header.column.getToggleSortingHandler(),
                        }}
                      >
                        {header.column.getIsSorted() && (
                          <Icon
                            as={ArrowUpIcon}
                            h="15px"
                            w="15px"
                            pos="absolute"
                            left="-15px"
                            transition="transform 0.2s ease"
                            transform={
                              header.column.getIsSorted() === "desc"
                                ? "rotate(180deg)"
                                : ""
                            }
                          />
                        )}

                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </Box>
                    </Th>
                  ))}
                  <Th w="125px"></Th>
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {table.getRowModel().rows.map((row) => (
                <Tr key={`row-${row.id}`}>
                  {row.getVisibleCells().map((cell) => (
                    <Td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>

          <Pagination table={table} />
        </>
      )}

      {!hasResult && (
        <EmptyList
          mt="25px"
          title={"Nenhum resultado encontrado"}
          description=""
          isTable
        />
      )}
    </>
  );
}
